<script setup lang="ts">
import { getUnnormalizedHyphenatedCurrentLocale } from '@@/bits/current_locale'
import { __ } from '@@/bits/intl'
import { getLocaleName } from '@@/bits/locale'
import { reload } from '@@/bits/location'
import OzDropdownButton, {
  OzDropdownButtonColorScheme,
  OzDropdownButtonSizePreset,
} from '@@/library/v4/components/OzDropdownButton.vue'
import { getAnchorFromElement } from '@@/library/v4/components/OzPopoverModal.vue'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { PopoverAnchor, VueCssClass } from '@@/types'
import { useLocale } from '@@/vuecomposables/locale'
import { uniqueId } from 'es-toolkit/compat'
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, ref } from 'vue'

const LocaleMenu = defineAsyncComponent(() => import('@@/vuecomponents/LocaleMenu.vue'))

withDefaults(
  defineProps<{
    buttonColorScheme?: OzDropdownButtonColorScheme
    darkMode?: 'auto' | boolean
    shouldBoldDropdownButtonText?: boolean
    /**
     * Customizes the gap color between the button and the focus ring.
     */
    dropdownButtonRingOffsetColorClasses?: VueCssClass
    /**
     * Customizes the text color on the button.
     */
    dropdownButtonTextColorClasses?: VueCssClass
  }>(),
  {
    buttonColorScheme: OzDropdownButtonColorScheme.SecondaryClearText,
    darkMode: 'auto',
    shouldBoldDropdownButtonText: false,
    dropdownButtonRingOffsetColorClasses: undefined,
    dropdownButtonTextColorClasses: undefined,
  },
)

const windowSizeStore = useWindowSizeStore()
const { isSmallerThanTabletPortrait, windowHeight } = storeToRefs(windowSizeStore)

/** Used for event tracking */
const emit = defineEmits<{
  (e: 'change-locale', lang: string): void
}>()

const { changeLocale } = useLocale()

const currentLocale = ref(getUnnormalizedHyphenatedCurrentLocale())
const isSelectorOpen = ref(false)
// Tracks whether the menu is actually mounted, so `aria-expanded` stays truthful even if the async
// chunk is still loading or fails to load (isSelectorOpen alone would report expanded prematurely).
const isMenuMounted = ref(false)
const menuAnchor = ref({} as Partial<PopoverAnchor>)
const localeSelector = ref<InstanceType<typeof OzDropdownButton>>()
const localeMenuDialogId = uniqueId('locale-menu-dialog-')
const currentLocaleName = computed(() => getLocaleName(currentLocale.value))

function updateMenuAnchor(): void {
  const el = localeSelector?.value?.$el
  if (!el) return
  menuAnchor.value = getAnchorFromElement(el, { top: -4, left: -4 })
}

function openMenu(): void {
  updateMenuAnchor()
  isSelectorOpen.value = true
}

function closeMenu(): void {
  isSelectorOpen.value = false
  isMenuMounted.value = false
}

async function handleChangeLocale(lang: string): Promise<void> {
  isSelectorOpen.value = false
  isMenuMounted.value = false

  if (currentLocale.value === lang) return

  currentLocale.value = lang
  await changeLocale(lang)
  emit('change-locale', lang)

  reload()
}

// Wait for the page fully rendered before (re)calculating the menu anchor
const onPageLoad = () => {
  updateMenuAnchor()
  window.removeEventListener('load', onPageLoad)
}
window.addEventListener('load', onPageLoad)

const ariaDescribedbyId = uniqueId('locale-selector-button-description-')
</script>

<template>
  <div>
    <OzDropdownButton
      ref="localeSelector"
      :aria-describedby="ariaDescribedbyId"
      :aria-expanded="isMenuMounted"
      :aria-controls="localeMenuDialogId"
      aria-haspopup="dialog"
      :color-scheme="buttonColorScheme"
      :size-preset="OzDropdownButtonSizePreset.H32px"
      :text="currentLocaleName"
      :dark-mode="darkMode"
      :should-bold-text="shouldBoldDropdownButtonText"
      :ring-offset-color-classes="dropdownButtonRingOffsetColorClasses"
      :text-color-classes="dropdownButtonTextColorClasses"
      data-testid="localeMenuButton"
      @click="openMenu"
      @keypress.stop.space="openMenu"
    />
    <p :id="ariaDescribedbyId" class="sr-only">{{ __('Selecting a different language will reload the page.') }}</p>

    <LocaleMenu
      v-if="isSelectorOpen"
      :anchor="menuAnchor"
      :current-locale="currentLocale"
      :is-phone="isSmallerThanTabletPortrait"
      :box-classes="['h-100']"
      :window-height="windowHeight"
      :id="localeMenuDialogId"
      @back="closeMenu"
      @cancel="closeMenu"
      @change="handleChangeLocale"
      @anchor-update="updateMenuAnchor"
      @vue:mounted="isMenuMounted = true"
    />
  </div>
</template>
